import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { GeneralPermissions } from "@redotech/redo-model/permissions";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { ActionRequiredSeverity } from "./action-required-notification";

export function useTiktokOmsReauthorization(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const canAuthenticateWithThirdParties = useHasPermission(
    GeneralPermissions.AUTHENTICATE_WITH_THIRD_PARTIES,
  );

  const expirationResult = useLoad(
    async (signal) => {
      if (!canAuthenticateWithThirdParties) {
        return null;
      }

      const result = await rpcClient.getTiktokAuthorizationExpiration(
        {},
        { signal },
      );
      return result.refreshTokenExpiresAt;
    },
    [rpcClient, canAuthenticateWithThirdParties],
  );

  if (expirationResult.pending || expirationResult.error) {
    return null;
  }

  const expirationDate = expirationResult.value;

  if (!expirationDate) {
    return null;
  }

  const oneMonthFromNow = new Date();
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

  if (expirationDate > oneMonthFromNow) {
    return null;
  }

  return {
    severity: ActionRequiredSeverity.Medium,
    item: {
      id: "tiktok-oms-reauthorization",
      type: "warning",
      message: "Your TikTok OMS connection needs to be reauthorized soon.",
      actions: [
        {
          actionType: "link",
          href: "https://services.tiktokshops.us/open/authorize?service_id=7498884683113793326",
          text: "Reauthorize",
          internal: false,
        },
      ],
    },
  };
}
